import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useShareTemplates } from "./hooks/useShareMessageTemplates";
import { RootState } from "../../../store/Index";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";
import { ShareTemplate } from "./types/share-template.types";
import CreateTemplateModal from "./components/CreateTemplateModal";

const ShareMessageConfiguration: React.FC = () => {
  const theme = useSelector((state: RootState) => state.theme);
  const { hasPermission } = usePermissions();
  const {
    templates,
    loading,
    error,
    fetchTemplates,
    deleteTemplate,
  } = useShareTemplates();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ShareTemplate | null>(
    null
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDeleteClick = (id: string) => {
    setDeleteTargetId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetId) {
      await deleteTemplate(deleteTargetId);
      setDeleteConfirmOpen(false);
      setDeleteTargetId(null);
    }
  };

  const handleEditTemplate = (template: ShareTemplate) => {
    setEditingTemplate(template);
    setCreateModalOpen(true);
  };

  const handleCreateClose = () => {
    setCreateModalOpen(false);
    setEditingTemplate(null);
  };

  const handleSaveTemplate = async () => {
    await fetchTemplates();
    handleCreateClose();
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Funeral":
        return theme.primary_colors[400];
      case "Church":
        return theme.primary_colors[200];
      case "Conference":
        return theme.primary_colors[600];
      default:
        return theme.primary_colors[500];
    }
  };

  if (loading && templates.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: theme.primary_colors[100], fontWeight: 600 }}
        >
          Share Message Templates
        </Typography>
        {hasPermission(PERMISSIONS.CONFIGURATION_ADD) && (
          <Button
            startIcon={<Add />}
            variant="contained"
            sx={{
              backgroundColor: theme.primary_colors[400],
              color: theme.primary_colors[900],
              "&:hover": { backgroundColor: theme.primary_colors[500] },
            }}
            onClick={() => {
              setEditingTemplate(null);
              setCreateModalOpen(true);
            }}
          >
            Add New Template
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {templates.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: theme.primary_colors[900],
            boxShadow: 0,
          }}
        >
          <Typography color={theme.primary_colors[300]}>
            No templates created yet. Click &quot;Add New Template&quot; to create
            your first one.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {templates.map((template) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={template.id}>
              <Card
                sx={{
                  backgroundColor: theme.primary_colors[900],
                  boxShadow: 0,

                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: theme.primary_colors[100], fontWeight: 600 }}
                    >
                      {template.name}
                    </Typography>
                    <Chip
                      label={template.category}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(template.category),
                        color: theme.primary_colors[900],
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.primary_colors[300],
                      mb: 1.5,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {template.body_template}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: "flex-end", pt: 0 }}>
                  {hasPermission(PERMISSIONS.CONFIGURATION_CHANGE) && (
                    <IconButton
                      size="small"
                      onClick={() => handleEditTemplate(template)}
                      sx={{ color: theme.primary_colors[400] }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                  {hasPermission(PERMISSIONS.CONFIGURATION_DELETE) && (
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(template.id)}
                      sx={{ color: theme.primary_colors[400] }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Modals */}
      <CreateTemplateModal
        open={createModalOpen}
        onClose={handleCreateClose}
        editTemplate={editingTemplate}
        onSave={handleSaveTemplate}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle sx={{ color: theme.primary_colors[100] }}>
          Delete Template
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: theme.primary_colors[900] }}>
          <Typography sx={{ color: theme.primary_colors[400] }}>
            Are you sure you want to delete this template?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{ color: theme.primary_colors[300] }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              backgroundColor: theme.primary_colors[400],
              color: theme.primary_colors[900],
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShareMessageConfiguration;
